@if (loading()) {
<app-page-loader loadingType="content" />
} @else {
<app-survey-header [survey]="null"></app-survey-header>
<div class="p-3 h-[calc(100%-var(--spacing)*15)]">
  <app-breadcrumb [items]="[{ label: 'Surveys', route: '/surveys' },{ label: 'Email Translations' }]"/>
  <div class="h-[calc(100%-var(--spacing)*9)] rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
    <div class="h-full w-full overflow-y-scroll">

      <!-- Toolbar -->
      <div class="sticky top-0 left-0 z-10 flex items-end gap-3 border-b border-stroke bg-white px-5 py-4 dark:border-strokedark dark:bg-gray-800 sm:px-7.5">
        <div class="flex-1">
          <label class="mb-1 block text-xs text-gray-500 dark:text-gray-400">Language</label>
          <select
            [(ngModel)]="selectedLanguage"
            (change)="requestLanguageChange()"
            [disabled]="loadingTranslations() || saving() || supportedLanguages.length === 0"
            class="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white disabled:opacity-60"
          >
            @for (language of supportedLanguages; track language.lang) {
            <option [value]="language.lang">{{ language.englishLabel }} ({{ language.lang }})</option>
            }
          </select>
        </div>

        @if (canEdit()) {
        <div class="flex items-center gap-2">
          @if (saveError()) {
          <span class="text-sm text-red-600">{{ saveError() }}</span>
          }
          @if (pendingChangesCount > 0) {
          <button type="button" (click)="requestDiscard()" [disabled]="saving()"
            class="rounded-lg border border-gray-400 bg-white px-3 py-1.5 text-md font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed">
            Discard
          </button>
          }
          <button type="button" (click)="requestSave()" [disabled]="pendingChangesCount === 0 || saving()"
            class="flex items-center gap-2 rounded-lg border border-blue-600 bg-blue-600 px-4 py-2 text-lg font-medium text-white hover:bg-white hover:text-blue-600
            disabled:opacity-50 disabled:bg-white disabled:border-gray-400 disabled:text-gray-600 disabled:hover:text-gray-600 disabled:cursor-not-allowed">
            @if (saving()) {
              <svg class="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/></svg>
              Saving...
            } @else {
              Save
              @if (pendingChangesCount > 0) {
              <span class="text-xs">({{ pendingChangesCount }} change{{ pendingChangesCount === 1 ? '' : 's' }})</span>
              }
            }
          </button>
        </div>
        }
      </div>

      <div class="p-3">
        @if (error()) {
        <p class="mb-3 rounded-lg border border-red-200 bg-red-100 p-3 text-sm text-red-600">{{ error() }}</p>
        }

        @if (loadingTranslations()) {
        <div class="flex justify-center py-16">
          <svg class="h-8 w-8 animate-spin text-blue-500" viewBox="0 0 24 24" fill="none"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/></svg>
        </div>
        } @else {
          @for (category of categories; track category.key) {
          <div class="mb-4 rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
            <div class="flex flex-wrap items-center justify-between gap-3 bg-blue-100 p-2 text-sm font-bold text-gray-700 dark:bg-gray-700 dark:text-gray-200">
              <div class="flex flex-wrap items-center gap-3">
                {{ category.label }}
                @if (category.key === 'survey' && surveyGroups.length > 0) {
                <select [(ngModel)]="selectedSurveyGroup"
                  class="h-8 max-w-md rounded-lg border border-gray-300 bg-white px-2 text-sm font-normal text-gray-800 dark:border-gray-700 dark:bg-gray-900 dark:text-white">
                  @for (group of surveyGroups; track group.key) {
                  <option [value]="group.key">{{ group.label }} — {{ group.count }}{{ group.changed ? ' · ' + group.changed + ' modified' : '' }}</option>
                  }
                </select>
                }
                @if (category.key === 'global' && translations.global.length > 0) {
                <div class="relative">
                  <input [(ngModel)]="globalFilter" type="text" placeholder="Filter by translation ID..."
                    class="h-8 w-64 rounded-lg border border-gray-300 bg-white pl-2 pr-7 text-sm font-normal text-gray-800 dark:border-gray-700 dark:bg-gray-900 dark:text-white"/>
                  @if (globalFilter) {
                  <button type="button" (click)="globalFilter = ''" title="Clear filter"
                    class="absolute right-1 top-1/2 -translate-y-1/2 rounded px-1.5 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">×</button>
                  }
                </div>
                }
              </div>
              <span class="text-xs font-normal text-gray-500 dark:text-gray-400">
                @if (category.key === 'survey') {
                {{ itemsFor('survey').length }} of {{ translations.survey.length }} items
                } @else if (globalFilter.trim()) {
                {{ itemsFor('global').length }} of {{ translations.global.length }} items
                } @else {
                {{ translations.global.length }} item{{ translations.global.length === 1 ? '' : 's' }}
                }
              </span>
            </div>

            <div class="divide-y divide-gray-100 dark:divide-gray-800">
              @for (translation of itemsFor(category.key); track translation.id) {
              <div class="p-3">
                <label class="mb-1.5 flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  <span class="font-mono">{{ translation.translationID }}</span>
                  @if (failedIds.has(translation.id)) {
                  <span class="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold text-red-700">SAVE FAILED</span>
                  } @else if (isChanged(translation)) {
                  <span class="rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-semibold text-orange-700">MODIFIED</span>
                  }
                </label>
                <textarea [(ngModel)]="translation.textValue" [disabled]="!canEdit()" rows="2"
                  class="w-full resize-y rounded-lg border bg-transparent px-4 py-2 text-sm dark:text-white disabled:opacity-60 disabled:cursor-not-allowed"
                  [class.border-red-500]="failedIds.has(translation.id)"
                  [class.border-orange-400]="!failedIds.has(translation.id) && isChanged(translation)"
                  [class.border-gray-300]="!failedIds.has(translation.id) && !isChanged(translation)"
                  [class.dark:border-gray-700]="!failedIds.has(translation.id) && !isChanged(translation)"></textarea>
              </div>
              } @empty {
                @if (category.key === 'global' && globalFilter.trim() && translations.global.length > 0) {
                <p class="px-3 py-6 text-center text-sm text-gray-400">No global translations match "{{ globalFilter.trim() }}".</p>
                } @else {
                <p class="px-3 py-6 text-center text-sm text-gray-400">No {{ category.label.toLowerCase() }} email translations for this language.</p>
                }
              }
            </div>
          </div>
          }
        }
      </div>
    </div>
  </div>
</div>
}

@if (showSaveConfirm()) {
<div class="fixed inset-0 z-50 flex items-center justify-center bg-black/40" (click)="cancelSave()">
  <div class="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl dark:bg-gray-900" (click)="$event.stopPropagation()">
    <h3 class="text-base font-semibold text-gray-800 dark:text-white">Save changes?</h3>
    <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
      {{ pendingChangesCount }} translation{{ pendingChangesCount === 1 ? '' : 's' }} will be saved for <span class="font-medium text-gray-700 dark:text-gray-300">{{ selectedLanguage }}</span>.
    </p>
    @if (saveError()) {
    <p class="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-500/10 dark:text-red-400">{{ saveError() }}</p>
    }
    <div class="mt-5 flex justify-end gap-3">
      <button type="button" (click)="cancelSave()" [disabled]="saving()" class="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800">
        Cancel
      </button>
      <button type="button" (click)="save()" [disabled]="saving()" class="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-70">
        @if (saving()) {
          <svg class="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/></svg>
          Saving...
        } @else {
          Save
        }
      </button>
    </div>
  </div>
</div>
}

@if (showSaveResult(); as result) {
<div class="fixed inset-0 z-50 flex items-center justify-center bg-black/40" (click)="closeSaveResult()">
  <div class="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl dark:bg-gray-900" (click)="$event.stopPropagation()">
    <h3 class="text-base font-semibold text-gray-800 dark:text-white">Save Result</h3>
    <div class="mt-3 rounded-lg px-3 py-2 text-sm" [class.bg-green-50]="result.failed.length === 0" [class.bg-amber-50]="result.failed.length > 0">
      @if (result.success > 0) {
      <span class="font-medium text-green-700">{{ result.success }} saved successfully.</span>
      }
      @if (result.failed.length > 0) {
      <span class="ml-1 font-medium text-red-700">{{ result.failed.length }} failed — highlighted in red.</span>
      }
    </div>
    @if (result.failed.length > 0) {
    <ul class="mt-3 max-h-48 space-y-1 overflow-y-auto text-xs text-gray-600 dark:text-gray-400">
      @for (f of result.failed; track f.id) {
      <li><span class="font-mono font-medium text-gray-800 dark:text-gray-200">{{ labelFor(f.id) }}</span>: {{ f.reason }}</li>
      }
    </ul>
    }
    <div class="mt-5 flex justify-end gap-3">
      <button type="button" (click)="closeSaveResult()" class="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700">
        Close
      </button>
    </div>
  </div>
</div>
}

@if (showLeaveConfirm()) {
<div class="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
  <div class="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl dark:bg-gray-900">
    <h3 class="text-base font-semibold text-gray-800 dark:text-white">Discard unsaved changes?</h3>
    <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
      You have {{ pendingChangesCount }} unsaved change{{ pendingChangesCount === 1 ? '' : 's' }}. If you leave now they will be lost.
    </p>
    <div class="mt-5 flex justify-end gap-3">
      <button type="button" (click)="cancelLeave()" class="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800">
        Stay
      </button>
      <button type="button" (click)="confirmLeave()" class="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700">
        Leave &amp; discard
      </button>
    </div>
  </div>
</div>
}

@if (showLanguageChangeConfirm()) {
<div class="fixed inset-0 z-50 flex items-center justify-center bg-black/40" (click)="cancelLanguageChange()">
  <div class="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl dark:bg-gray-900" (click)="$event.stopPropagation()">
    <h3 class="text-base font-semibold text-gray-800 dark:text-white">Switch language?</h3>
    <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
      You have {{ pendingChangesCount }} unsaved change{{ pendingChangesCount === 1 ? '' : 's' }} for <span class="font-medium text-gray-700 dark:text-gray-300">{{ previousLanguage }}</span>. Switching language will discard them.
    </p>
    <div class="mt-5 flex justify-end gap-3">
      <button type="button" (click)="cancelLanguageChange()" class="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800">
        Cancel
      </button>
      <button type="button" (click)="confirmLanguageChange()" class="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700">
        Switch &amp; discard
      </button>
    </div>
  </div>
</div>
}

@if (showDiscardConfirm()) {
<div class="fixed inset-0 z-50 flex items-center justify-center bg-black/40" (click)="cancelDiscard()">
  <div class="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl dark:bg-gray-900" (click)="$event.stopPropagation()">
    <h3 class="text-base font-semibold text-gray-800 dark:text-white">Discard changes?</h3>
    <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
      {{ pendingChangesCount }} unsaved change{{ pendingChangesCount === 1 ? '' : 's' }} will be lost and translations will be restored to their last saved state.
    </p>
    <div class="mt-5 flex justify-end gap-3">
      <button type="button" (click)="cancelDiscard()" class="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800">
        Cancel
      </button>
      <button type="button" (click)="discard()" class="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700">
        Discard
      </button>
    </div>
  </div>
</div>
}
